import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { discoveryFeedApi } from '../../api/discoveryFeedApi';
import { discoveryPreferencesApi } from '../../api/discoveryPreferencesApi';
import { useAuth } from '../../auth/AuthContext';
import { authReturnLocation } from '../../auth/authReturn';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import SafeImage from '../../components/SafeImage';
import PostEngagementBar from '../social-post/PostEngagementBar';
import PostMediaGallery from '../social-post/PostMediaGallery';
import { travelMemberProductDisplayName } from '../member-identity/memberIdentityPresentation';
import { socialPostKindProductLabel } from '../social-post/socialPostKindPresentation';
import { SOCIAL_ACTION_PRODUCT_CHROME as socialChrome } from '../social-post/socialActionPresentation';
import SocialQuoteReferenceCard from '../social-post/SocialQuoteReferenceCard';
import { canShowFeedPreferenceActions } from './discoverModel';
import { isMapCapableItem } from './explorePostsSelection';
import { useContentVisibilityTelemetry } from './useContentVisibilityTelemetry';
import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import './explore.css';
import '../social-compose/social-compose.css';
const SOCIAL_POST_CONTENT_TYPE = 'SOCIAL_POST';

function timeLabel(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function Avatar({ author, productName }) {
  const [broken, setBroken] = useState(false);
  const initial = String(productName || '旅').slice(0, 1).toUpperCase();
  return author.avatarUrl && !broken
    ? <img className="discovery-card__avatar" src={author.avatarUrl} alt="" onError={() => setBroken(true)} />
    : <span className="discovery-card__avatar discovery-card__avatar--fallback" aria-hidden="true">
      {initial}
    </span>;
}

/**
 * Social discovery card with canonical NI / Hide / Mute preference actions.
 */
export default function SocialDiscoveryCard({
  item,
  onEngagementChange,
  onNotInterested,
  onMute,
  onHide,
  onShowOnMap,
  returnTo,
  feedSessionId,
  sponsored = false,
  commentHref,
  onFlushImpression,
  onFlushBehavior,
}) {
  const { isAuthenticated, user } = useAuth();
  const { t } = useTravelTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = useState(null);
  const [actionError, setActionError] = useState('');
  const detailState = returnTo ? { from: { pathname: returnTo } } : undefined;
  const kindLabel = socialPostKindProductLabel(item.kind);
  const authorProductName = travelMemberProductDisplayName(item.author);
  const mapCapable = typeof onShowOnMap === 'function' && isMapCapableItem(item);

  const viewerMemberId = user?.memberId ?? user?.id;
  const creatorMemberId = item.author?.memberId;
  const showPreferenceActions = isAuthenticated && canShowFeedPreferenceActions({
    viewerMemberId,
    creatorMemberId,
  });

  // For You 由面板提供 telemetry callback（含匿名 session）；其他 surface 維持僅登入時送出
  const panelTelemetry = typeof onFlushImpression === 'function';
  const { ref } = useContentVisibilityTelemetry({
    enabled: panelTelemetry || isAuthenticated,
    contentType: SOCIAL_POST_CONTENT_TYPE,
    contentId: String(item.postId),
    feedSessionId,
    onFlushBehavior: onFlushBehavior
      || ((payload) => { discoveryFeedApi.recordBehavior(payload).catch(() => {}); }),
    onFlushImpression: onFlushImpression
      || ((payload) => { discoveryFeedApi.recordImpression(payload).catch(() => {}); }),
  });

  function requireAuth() {
    if (isAuthenticated) return true;
    navigate('/login', { state: { from: authReturnLocation(location) } });
    return false;
  }

  async function runPreferenceAction(actionKey, apiCall, onSuccess) {
    if (!requireAuth() || busy) return;
    setBusy(actionKey);
    setActionError('');
    try {
      await apiCall();
      onSuccess?.();
    } catch (cause) {
      setActionError(resolveSafeUserVisibleError(cause, t, 'travel.discover.error.actionFailed'));
    } finally {
      setBusy(null);
    }
  }

  const preferenceMoreItems = showPreferenceActions ? [
    {
      id: 'not-interested',
      label: t('travel.discover.notInterested'),
      disabled: Boolean(busy),
      onSelect: () => {
        runPreferenceAction('notInterested', () => discoveryPreferencesApi.markNotInterested({
          contentType: SOCIAL_POST_CONTENT_TYPE,
          contentId: String(item.postId),
        }), () => onNotInterested?.(item.postId));
      },
    },
    {
      id: 'mute',
      label: t('travel.discover.mute'),
      disabled: Boolean(busy) || !creatorMemberId,
      onSelect: () => {
        if (!creatorMemberId) return;
        runPreferenceAction('mute', () => discoveryPreferencesApi.muteCreator(creatorMemberId), () => onMute?.(creatorMemberId));
      },
    },
    {
      id: 'hide',
      label: t('travel.discover.hideContent'),
      disabled: Boolean(busy),
      onSelect: () => {
        runPreferenceAction('hide', () => discoveryPreferencesApi.hideContent({
          contentType: SOCIAL_POST_CONTENT_TYPE,
          contentId: String(item.postId),
        }), () => onHide?.(item.postId));
      },
    },
  ] : [];

  return (
    <article ref={ref} className="discovery-card" data-testid={`discovery-card-${item.postId}`}>
      <header className="discovery-card__header">
        <Link className="discovery-card__author" to={`/social/creators/${item.author.memberId}`}>
          <Avatar author={item.author} productName={authorProductName} />
          <span><strong>{authorProductName}</strong><time dateTime={item.publishedAt || undefined}>{timeLabel(item.publishedAt)}</time></span>
        </Link>
        <span className="discovery-card__kind">{kindLabel}</span>
        {sponsored ? (
          <span className="for-you-card__sponsored-badge" data-testid="discovery-card-sponsored-badge">
            {t('travel.discover.sponsored')}
          </span>
        ) : null}
      </header>
      <Link className="discovery-card__post-link" to={`/social/posts/${item.postId}`} state={detailState} aria-label={socialChrome.openPostAria(authorProductName)}>
        {item.text ? <p className="discovery-card__text">{item.text}</p> : null}
      </Link>
      <PostMediaGallery
        media={item.media}
        href={`/social/posts/${item.postId}`}
        alt={`Travel story by ${authorProductName}`}
      />
      {item.location ? (
        <p className="discovery-card__location">{item.location.displayName}</p>
      ) : null}
      {mapCapable ? (
        <button
          type="button"
          className="discovery-card__map-cta"
          data-testid={`discovery-show-on-map-${item.postId}`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onShowOnMap(item);
          }}
        >
          {t('travel.discover.posts.showOnMap')}
        </button>
      ) : null}
      {item.journey ? (
        <Link className="discovery-card__journey" to={`/public/journeys/${item.journey.publicationId}`}>
          <SafeImage className="discovery-card__journey-cover" src={item.journey.coverUrl} alt="" />
          <span>
            <small>{t('travel.discover.posts.publicJourney')}</small>
            <strong>{item.journey.title}</strong>
            {item.journey.summary ? <em>{item.journey.summary}</em> : null}
            <em>{t('travel.discover.posts.openPublishedJourney')}</em>
          </span>
        </Link>
      ) : item.journeyUnavailable ? (
        <div className="discovery-card__journey discovery-card__journey--unavailable">
          <span><small>{t('travel.discover.posts.publicJourney')}</small><strong>{t('travel.discover.posts.journeyUnavailable')}</strong></span>
        </div>
      ) : null}
      {item.quote ? (
        <SocialQuoteReferenceCard quote={item.quote} className="discovery-card__quote" />
      ) : null}
      <PostEngagementBar
        postId={item.postId}
        engagement={item.engagement}
        detailTo={`/social/posts/${item.postId}`}
        detailState={detailState}
        commentHref={commentHref}
        shareTitle={item.text || 'Travel story'}
        onEngagementChange={onEngagementChange}
        moreItems={preferenceMoreItems}
        className="discovery-card__actions"
      />
      {actionError ? <p className="discovery-card__action-error" role="alert">{actionError}</p> : null}
    </article>
  );
}
